import {appAPI} from "../../../api";
import {IFleetScore, IMachineryScore, IMachineryContext, IPeriodScore, IDayScore, ITrendBucket, ScorePeriod, TrendGranularity} from "../../../models/IDrivingScore";

const basePath = `/aggressive-driving-events`;

/** Гранулярность тренда = выбранный период 1:1 (дни/недели/месяцы). */
export function granularityForPeriod(period: ScorePeriod): TrendGranularity {
    return period;
}

function isoRange(from: Date, to: Date) {
    return {from: from.toISOString(), to: to.toISOString()};
}

// Горизонты окна тренда. Берём только последние осмысленные периоды, а не год+
// пустоты: владельцу для решений о премиях нужны последние недели/месяцы.
// Узкое окно заодно убирает «артефактную» корзину — одиночную старую запись,
// которой слишком широкое окно лепило отдельный столбец.
const TREND_DAYS_BACK = 60; // дни → последние 60 дней
const TREND_WEEK_MODE_MONTHS_BACK = 6; // недельный режим смотрит на 6 месяцев назад
const TREND_MONTH_MODE_MONTHS_BACK = 12; // месячный режим — на 12 месяцев назад

/** Окно тренда по периоду (дни/недели/месяцы) — общее для машины и парка. */
function trendRange(period: ScorePeriod): { from: string; to: string } {
    const to = new Date();
    const from = new Date(to);
    switch (period) {
        case 'day':   from.setDate(from.getDate() - TREND_DAYS_BACK); break;
        case 'week':  from.setMonth(from.getMonth() - TREND_WEEK_MODE_MONTHS_BACK); break;
        case 'month': from.setMonth(from.getMonth() - TREND_MONTH_MODE_MONTHS_BACK); break;
    }
    return isoRange(from, to);
}

/** Окно тренда ПАРКА: последние N периодов (см. trendRange). */
function fleetTrendRange(period: ScorePeriod): { from: string; to: string } {
    return trendRange(period);
}

/** Окно тренда МАШИНЫ: последние N периодов (см. trendRange). */
function machineryTrendRange(period: ScorePeriod): { from: string; to: string } {
    return trendRange(period);
}

/**
 * Окно ПОСЛЕДНЕЙ КОРЗИНЫ — для общего балла и таблицы машин (п.5):
 * дни → текущий день, недели → последние 7 дней, месяцы → последние 30 дней.
 */
function latestBucketRange(period: ScorePeriod): { from: string; to: string } {
    const to = new Date();
    const from = new Date(to);
    switch (period) {
        case 'day':   from.setHours(0, 0, 0, 0); break;
        case 'week':  from.setDate(from.getDate() - 7); break;
        case 'month': from.setDate(from.getDate() - 30); break;
    }
    return isoRange(from, to);
}

export const drivingScoreAPI = {
    /** Балл парка + таблица машин за ПОСЛЕДНЮЮ корзину (текущий день/неделя/месяц), п.5. */
    getFleetScore: async (
        company_id: string,
        period: ScorePeriod,
    ): Promise<IFleetScore> => {
        const {from, to} = latestBucketRange(period);
        const res = await appAPI.get(`${basePath}/company/${company_id}/score`, {
            params: {from, to},
        });
        return res.data;
    },

    getMachineryScore: async (
        machinery_id: string,
        period: ScorePeriod,
    ): Promise<IPeriodScore> => {
        const {from, to} = latestBucketRange(period);
        const res = await appAPI.get(`${basePath}/machinery/${machinery_id}/score`, {
            params: {from, to},
        });
        return res.data;
    },

    getMachineryDailyScore: async (
        machinery_id: string,
        period: ScorePeriod,
    ): Promise<IDayScore[]> => {
        const {from, to} = machineryTrendRange(period);
        const res = await appAPI.get(
            `${basePath}/machinery/${machinery_id}/score/daily`,
            {params: {from, to}},
        );
        return Array.isArray(res.data) ? res.data : (res.data.items ?? []);
    },

    /** Тренд машины: окно по п.4 (дни=60дн, недели/месяцы=год), корзина=период. */
    getMachineryTrend: async (
        machinery_id: string,
        period: ScorePeriod,
    ): Promise<ITrendBucket[]> => {
        const {from, to} = machineryTrendRange(period);
        const granularity = granularityForPeriod(period);
        const res = await appAPI.get(
            `${basePath}/machinery/${machinery_id}/score/trend`,
            {params: {from, to, granularity}},
        );
        return Array.isArray(res.data) ? res.data : (res.data.items ?? []);
    },

    /** Тренд парка: окно всегда год (п.3), корзина=период. */
    getFleetTrend: async (
        company_id: string,
        period: ScorePeriod,
    ): Promise<ITrendBucket[]> => {
        const {from, to} = fleetTrendRange(period);
        const granularity = granularityForPeriod(period);
        const res = await appAPI.get(
            `${basePath}/company/${company_id}/score/trend`,
            {params: {from, to, granularity}},
        );
        return Array.isArray(res.data) ? res.data : (res.data.items ?? []);
    },

    /** Контекст машины (ночное+превышения) за ТО ЖЕ окно, что тренд машины. */
    getMachineryContext: async (
        machinery_id: string,
        period: ScorePeriod,
    ): Promise<IMachineryContext> => {
        const {from, to} = machineryTrendRange(period);
        const res = await appAPI.get(
            `${basePath}/machinery/${machinery_id}/score/context`,
            {params: {from, to}},
        );
        return res.data;
    },
};

export type {IFleetScore, IMachineryScore};